/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import IconView, {
  IconViewProps,
} from '@airasia-phoenix/react-native/components/IconView';
import TextView, {
  TextViewProps,
} from '@airasia-phoenix/react-native/components/TextView';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { SSO_SIGNUP_PROVIDER_ORDER_DEFAULT } from '../../constants/featureFlags';
import { SocialProvider } from '../../managers';
import { resolveSocialProviderOrder } from '../../utils/socialProviders';
import styles from './styles';

export type SocialProviderConfig = {
  icon: IconViewProps;
  label: TextViewProps;
};

export type SocialProvidersViewProps = {
  providers: Partial<Record<SocialProvider, Partial<SocialProviderConfig>>>;
  getLabel: (provider: SocialProvider) => string;
  order?: SocialProvider[]; // left-to-right; defaults to Facebook, Google, Apple, WeChat
  onProviderPress?: (provider: SocialProvider) => void;
  testID?: string;
};

export function SocialProvidersView({
  providers,
  getLabel,
  order,
  onProviderPress,
  testID,
}: SocialProvidersViewProps) {
  const providerOrder = useMemo(
    () =>
      resolveSocialProviderOrder(order ?? SSO_SIGNUP_PROVIDER_ORDER_DEFAULT),
    [order],
  );

  return (
    <View style={styles.row} testID={testID}>
      {providerOrder.map((provider) => {
        const config = providers[provider];

        // Skip hidden/unconfigured providers so the row re-centres.
        if (!config?.label) {
          return null;
        }

        return (
          <View key={provider} style={styles.item}>
            <IconView
              {...config.icon}
              onPress={
                onProviderPress
                  ? () => onProviderPress(provider)
                  : config.icon?.onPress
              }
            />
            <View style={styles.gap} />
            <TextView {...config.label} text={getLabel(provider)} />
          </View>
        );
      })}
    </View>
  );
}
